import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { CdsPopupService } from '@cds/ng-web-components/popup';
import { CdsLangService } from '@cds/ng-core/lang';
import { DateTime } from 'luxon';

import { Session } from '../../../core/models/user';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { addSessionByKey } from '../../../utils/indexdb';
import { UserAgreementPopupComponent } from 'src/app/shared/user-agreement-popup/user-agreement-popup.component';
import { IffService } from '../../salesjourney/service/iff.service';
import { SessionConflictService } from '../../../core/services/session-conflict.service';
import { LanguageChangeService } from '../../../shared/language-change.service';

@Component({
  selector: 'app-mcf-mobile-login',
  templateUrl: './mcf-mobile-login.component.html',
})
export class McfMobileLoginComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdsPopup = inject(CdsPopupService);
  private authenticationService = inject(AuthenticationService);
  private langService = inject(CdsLangService);
  private iffService = inject(IffService);
  private sessionConflictService = inject(SessionConflictService);
  private languageChangeService = inject(LanguageChangeService);

  ngOnInit(): void {
    const state = this.route.snapshot.queryParamMap.get('state') || '';
    const code = this.route.snapshot.queryParamMap.get('code') || '';
    const rawRedirection = this.route.snapshot.queryParamMap.get('redirection') || '';
    let redirection: string;
    try {
      redirection = rawRedirection ? decodeURIComponent(rawRedirection) : '';
    } catch {
      redirection = rawRedirection;
    }
    const lang = this.route.snapshot.queryParamMap.get('lang') || '';

    if (lang === 'zh-hant') {
      this.languageChangeService.setLang('zh');
    } else if (lang === 'en') {
      this.languageChangeService.setLang('en');
    }

    if (!state || !code) {
      this.router.navigate(['/user/login']);
      return;
    }

    const popupRef = this.authenticationService.getLoadingDialogRef();
    const redirectionForApi = redirection ? encodeURIComponent(redirection) : '';
    this.authenticationService.authenticateMcfMobile(state, code, redirectionForApi, lang).subscribe({
      next: async res => {
        popupRef.close();
        if (res.result === 0) {
          const userInfo: Session = res.data;
          if (userInfo) {
            userInfo.currentLastUpdate = DateTime.now().toMillis();

            const hasConflict = await this.sessionConflictService.hasConflict(userInfo.id);
            if (hasConflict) {
              await this.sessionConflictService.showConflictDialog();
            }

            await addSessionByKey(userInfo).then(() => {
              this.sessionConflictService.broadcastSessionChange(userInfo.id);
              this.iffService.getServicingAgentCodeAndChannel();
              const targetPath = redirection || '/home';
              this.router.navigateByUrl(targetPath);
            });
          }
          localStorage.setItem('loginType', 'mcf');
        } else {
          let msg: string;
          switch (res.result) {
            case 2008:
              msg = 'login.errorMsg.mcf2008';
              break;
            case 2019:
              msg = 'login.errorMsg.mcf2019';
              break;
            default:
              msg = '<span class="cds-h2-light l-pt-2">' + res.message + '</span>';
              break;
          }
          this.handleError(msg, popupRef);
        }
      },
      error: err => {
        this.handleError(err.message, popupRef);
      },
    });
  }

  private handleError(message: string, popupRef: MatDialogRef<UserAgreementPopupComponent>) {
    popupRef.close();
    const msg = message ? message : 'Unknown Error';
    this.langService.addLangEntries({
      'mcfMobile.backToLogin': {
        en: 'Back to Login Page',
        zh: '返回登錄頁',
      },
    });
    const errorPopupRef: MatDialogRef<UserAgreementPopupComponent> = this.cdsPopup.open(UserAgreementPopupComponent, {
      size: 'md',
      data: {
        message: msg,
        continue: 'mcfMobile.backToLogin',
        type: 'alert',
      },
    });
    errorPopupRef.afterClosed().subscribe(confirm => {
      if (confirm && confirm.agree) {
        this.router.navigate(['/user/login']);
      }
    });
  }
}
